import { GoogleGenerativeAI } from '@google/generative-ai'
import { 
  GeminiChatRequest, 
  GeminiChatResponse, 
  GeminiConfig,
  FinancialProfile,
  BudgetRecommendation,
  BudgetCategory,
  ChatMessage
} from '../types/ai-budget'
import { QuizQuestion } from '../types/learning'

class GeminiService {
  private genAI: GoogleGenerativeAI | null = null
  private model: any = null
  private config: GeminiConfig
  private isInitialized: boolean = false
  private initializationPromise: Promise<void> | null = null

  constructor() {
    // Get API key from environment variables
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY

    this.config = {
      apiKey: apiKey || '',
      model: '', // Will be auto-detected
      temperature: 0.7,
      maxTokens: 2048,
      systemPrompt: this.getSystemPrompt()
    }

    if (apiKey) {
      this.genAI = new GoogleGenerativeAI(apiKey)
      // Start initialization process
      this.initializationPromise = this.initializeWithWorkingModel()
    } else {
      console.warn('Gemini API key not found in environment variables')
    }
  }

  // Auto-detect and initialize with a working model
  private async initializeWithWorkingModel(): Promise<void> {
    if (!this.genAI) {
      console.error('GoogleGenerativeAI not initialized')
      return
    }

    try {
      console.log('Initializing Gemini service...')
      
      // First, try to get available models from the API
      const workingModels = await this.discoverAvailableModels()
      
      if (workingModels.length > 0) {
        // Prioritize cheaper models first
        const bestModel = this.selectBestModel(workingModels)
        this.config.model = bestModel
        this.model = this.genAI.getGenerativeModel({ model: bestModel })
        this.isInitialized = true
        console.log(`Gemini service initialized with model: ${bestModel}`)
        console.log(`Model cost tier: ${this.getModelCostInfo(bestModel)}`)
      } else {
        console.error('No working Gemini models found')
        this.isInitialized = false
      }
    } catch (error) {
      console.error('Failed to initialize Gemini service:', error)
      this.isInitialized = false
    }
  }

  // Discover available models that support generateContent
  private async discoverAvailableModels(): Promise<string[]> {
    if (!this.genAI) return []

    try {
      // First, get the list of available models
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${this.config.apiKey}`
      )

      if (!response.ok) {
        console.error('Failed to list models:', response.status, response.statusText)
        return []
      }

      const data = await response.json()
      const availableModels = data.models || []

      // Filter models that support generateContent
      const generateContentModels = availableModels.filter((model: any) => 
        model.supportedGenerationMethods?.includes('generateContent')
      )

      const workingModels: string[] = []

      // Test each model to ensure it actually works
      for (const modelInfo of generateContentModels.slice(0, 3)) { // Test up to 3 models
        const modelName = modelInfo.name
        
        try {
          console.log(`Testing model: ${modelName}`)
          
          const testModel = this.genAI.getGenerativeModel({ model: modelName })
          const result = await testModel.generateContent('Hello')
          const response = await result.response
          await response.text()
          
          console.log(`${modelName} - Working`)
          workingModels.push(modelName)
        } catch (error: any) {
          console.log(`${modelName} - Failed:`, error.message.substring(0, 50))
        }
      }

      return workingModels
    } catch (error) {
      console.error('Error discovering models:', error)
      return []
    }
  }

  // Select ONLY cheapest Flash models (prioritize 1.5, fallback to 2.5 Flash if needed)
  private selectBestModel(availableModels: string[]): string {
    console.log('Searching for CHEAPEST Flash models (1.5 preferred, 2.5 Flash fallback):', availableModels)
    
    // Prioritize 1.5 Flash first (absolute cheapest if available)
    const preferred15Models = [
      'models/gemini-1.5-flash',           
      'models/gemini-1.5-flash-latest',        
      'models/gemini-1.5-flash-001',       
      'gemini-1.5-flash',                  
      'gemini-1.5-flash-latest',           
      'gemini-1.5-flash-001'               
    ]

    // Check for 1.5 Flash models first
    for (const flashModel of preferred15Models) {
      if (availableModels.includes(flashModel)) {
        console.log(`Selected PREFERRED 1.5 Flash: ${flashModel} ($0.35/$1.05 per 1M tokens - cheapest option)`)
        return flashModel
      }
    }

    // If no 1.5 Flash available, fallback to 2.5 Flash (still cheap, just newer)
    const fallback25FlashModels = [
      'models/gemini-2.5-flash',
      'models/gemini-2.5-flash-preview-05-20',
      'gemini-2.5-flash'
    ]

    for (const flashModel of fallback25FlashModels) {
      if (availableModels.includes(flashModel)) {
        console.log(`Selected FALLBACK 2.5 Flash: ${flashModel} (Still budget-friendly Flash model)`)
        console.warn(`Note: Using 2.5 Flash because 1.5 Flash not available. Still cost-optimized!`)
        return flashModel
      }
    }

    // If no Flash models at all, throw error
    console.error('NO FLASH MODELS AVAILABLE!')
    console.error('Available models:', availableModels)
    console.error('Note: Only Flash models are allowed for cost control')
    throw new Error(
      'Cost Control: ONLY Flash models (1.5 or 2.5) are allowed for budget reasons. ' +
      'No Flash models found in available models: ' + availableModels.join(', ')
    )
  }

  // Get cost information for a model
  private getModelCostInfo(modelName: string): string {
    const model = modelName.toLowerCase()
    
    if (model.includes('1.5-flash')) {
      return 'CHEAPEST ($0.35/$1.05 per 1M tokens - Gemini 1.5 Flash preferred)'
    } else if (model.includes('2.5-flash')) {
      return 'BUDGET-FRIENDLY (2.5 Flash - still cost-optimized fallback)'
    } else if (model.includes('1.5-pro')) {
      return 'Expensive ($7/$21 per 1M tokens - BLOCKED)'
    } else if (model.includes('2.5-pro')) {
      return 'Very expensive (Pro model - BLOCKED)'
    } else if (model.includes('pro')) {
      return 'Expensive (Pro model - BLOCKED)'
    } else {
      return 'Unknown pricing (Only Flash models allowed)'
    }
  }

  private getSystemPrompt(): string {
    return `You are Kubera, a friendly and conversational financial advisor assistant for Svarno (a website thats like a financial advisor/agency. the user is NOT Svarno). Your goal is to help users create personalized budgets through natural, inquisitive conversation.

CRITICAL CONVERSATION STYLE:
1. **Be Concise**: Keep responses SHORT (2-4 sentences max). Users find long responses overwhelming.
2. **Be Inquisitive**: Ask follow-up questions. Don't jump straight to a budget - learn about their situation first.
3. **One Thing at a Time**: Ask about one topic per message, then wait for their response.
4. **Be Conversational**: Talk like a friend, not a textbook. Use natural language.

CONVERSATION FLOW (Take Your Time!):
1. **Income Discovery**: Start by asking about monthly income
2. **Current Expenses**: Ask what their biggest expenses are (housing, food, etc.)
3. **Financial Situation**: Are they in debt? Do they have savings already?
4. **Goals & Dreams**: What are they saving/investing for? (house, retirement, financial freedom?)
5. **Risk Tolerance**: Are they comfortable with investment risk?
6. **ONLY THEN**: Generate the complete budget recommendation

KEY RULES:
- Never generate a budget until you've asked at least 3-4 follow-up questions
- Keep each response under 100 words
- Ask ONE question at a time
- Be encouraging and supportive
- Use simple language (avoid jargon like "liquidity" or "asset allocation")
- When you DO create a budget, prioritize saving (10-15%) and investing (10-20%)

Remember: You're having a conversation, not giving a lecture. Keep it brief and engaging!`
  }

  private getEnhancedSystemPrompt(): string {
    return `${this.getSystemPrompt()}

WHEN TO CREATE A BUDGET:
- Only after you've gathered sufficient information through conversation
- Minimum info needed: income, major expenses, financial goals, some idea of risk tolerance
- If they provide everything upfront, still ask 1-2 clarifying questions before creating the budget

BUDGET CREATION RULES:
When you DO create a budget, keep the intro SHORT (1-2 sentences), then provide the structured plan.

CRITICAL: Your budget recommendations MUST prioritize BOTH savings AND investments:
- Savings: At least 10-15% for emergency fund and short-term goals
- Investments: At least 10-20% for long-term wealth building (retirement, index funds, stocks)
- Combined savings + investments should be 20-35% of income minimum

BUDGET FORMAT (MUST FOLLOW EXACTLY):
Start with: BUDGET_RECOMMENDATION_START
Then format like this:

**Monthly Budget Plan ($X,XXX income):**

**Essential Expenses:**
- Housing: $X,XXX (XX%)
- Food: $X,XXX (XX%)
- Transportation: $X,XXX (XX%)
- Utilities & Healthcare: $X,XXX (XX%)

**Discretionary:**
- Entertainment & Personal: $X,XXX (XX%)

**Savings & Investment:**
- Emergency Savings: $X,XXX (XX%)
- Retirement Savings (401k/IRA): $X,XXX (XX%)
- Investment Portfolio: $X,XXX (XX%)

End with: BUDGET_RECOMMENDATION_END

AFTER the budget, add a brief note (2-3 sentences) about next steps or key priorities.

FORMAT REQUIREMENTS:
- Each category on its own line: "- Category Name: $Amount (Percentage%)"
- Use commas in amounts: $1,250
- Percentages should add up to ~100%
- Keep explanations brief and actionable`
  }

  async sendChatMessage(request: GeminiChatRequest): Promise<GeminiChatResponse> {
    // Wait for initialization to complete
    if (this.initializationPromise) {
      await this.initializationPromise
    }

    if (!this.model || !this.isInitialized) {
      throw new Error('Gemini service not initialized. Please check your API key and internet connection.')
    }

    try {
      // Prepare the conversation context with system prompt
      const conversationHistory = this.formatConversationHistory(request.context || [])
      const systemPrompt = this.getEnhancedSystemPrompt()
      
      let fullPrompt = ''
      if (conversationHistory) {
        fullPrompt = `${systemPrompt}\n\nConversation History:\n${conversationHistory}\n\nUser: ${request.message}`
      } else {
        fullPrompt = `${systemPrompt}\n\nUser: ${request.message}`
      }

      // Send request to Gemini
      const result = await this.model.generateContent(fullPrompt)
      const response = await result.response
      const text = response.text()

      // Check if this response contains a budget recommendation
      const budgetRecommendation = this.extractBudgetRecommendation(text, request.context || [])
      
      // Clean the message by removing internal markers before displaying to user
      const cleanMessage = this.cleanMessageForDisplay(text)

      return {
        message: cleanMessage,
        budgetRecommendation, // Include structured budget data if detected
        metadata: {
          model: this.config.model,
          timestamp: new Date().toISOString(),
          userId: request.userId,
          sessionId: request.sessionId,
          containsBudget: !!budgetRecommendation
        }
      }
    } catch (error) {
      console.error('Gemini API error:', error)
      throw new Error(`Failed to get AI response: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async generateBudgetRecommendation(financialProfile: FinancialProfile): Promise<BudgetRecommendation> {
    // Wait for initialization to complete
    if (this.initializationPromise) {
      await this.initializationPromise
    }

    if (!this.model || !this.isInitialized) {
      throw new Error('Gemini service not initialized. Please check your API key and internet connection.')
    }

    try {
      const prompt = this.createBudgetAnalysisPrompt(financialProfile)
      
      const result = await this.model.generateContent(prompt)
      const response = await result.response
      const text = response.text()

      // Parse the structured response
      return this.parseBudgetRecommendation(text, financialProfile)
    } catch (error) {
      console.error('Budget generation error:', error)
      throw new Error(`Failed to generate budget recommendation: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  private formatConversationHistory(messages: ChatMessage[]): string {
    return messages
      .map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
      .join('\n')
  }

  /**
   * Extract structured budget recommendation from AI response
   */
  private extractBudgetRecommendation(aiResponse: string, conversationHistory: ChatMessage[]): BudgetRecommendation | null {
    console.log('GeminiService: Checking for budget recommendation in response...')
    
    // Check if response contains budget recommendation markers
    const hasStartMarker = aiResponse.includes('BUDGET_RECOMMENDATION_START')
    const hasEndMarker = aiResponse.includes('BUDGET_RECOMMENDATION_END')
    
    if (!hasStartMarker || !hasEndMarker) {
      console.log('GeminiService: No budget recommendation markers found')
      return null
    }

    console.log('GeminiService: Budget recommendation detected, extracting data...')

    try {
      // Extract the budget section more reliably
      const startIndex = aiResponse.indexOf('BUDGET_RECOMMENDATION_START')
      const endIndex = aiResponse.indexOf('BUDGET_RECOMMENDATION_END')
      
      if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
        console.log('GeminiService: Invalid marker positions')
        return null
      }

      const budgetSection = aiResponse.substring(startIndex + 'BUDGET_RECOMMENDATION_START'.length, endIndex)
      console.log('GeminiService: Extracted budget section:', budgetSection.substring(0, 200) + '...')

      // Try to extract monthly income from conversation history or response
      const monthlyIncome = this.extractMonthlyIncome(conversationHistory, aiResponse)
      console.log('GeminiService: Detected monthly income:', monthlyIncome)
      
      // Parse budget categories from the response with improved logic
      const budgetCategories = this.parseBudgetCategories(budgetSection, monthlyIncome)
      console.log('GeminiService: Parsed categories count:', budgetCategories.length)

      if (budgetCategories.length === 0) {
        console.log('GeminiService: No budget categories could be parsed from budget section')
        console.log('GeminiService: Trying to parse from full AI response as fallback...')
        
        // Try parsing the entire response if markers didn't contain categories
        const fallbackCategories = this.parseBudgetCategories(aiResponse, monthlyIncome)
        
        if (fallbackCategories.length === 0) {
          console.error('GeminiService: Parsing completely failed. Budget section preview:', budgetSection.substring(0, 500))
          console.error('GeminiService: AI did not provide parseable budget data. Returning null.')
          return null
        }
        
        console.log('GeminiService: Found', fallbackCategories.length, 'categories in full response')
        budgetCategories.push(...fallbackCategories)
      }

      // Log what we actually parsed
      console.log('GeminiService: Parsed categories:', budgetCategories.map(c => `${c.name}: $${c.allocated} (${c.percentage}%)`))

      // Validate minimum requirements (but don't add defaults, just warn)
      const hasEssential = budgetCategories.some(cat => cat.type === 'essential')
      const hasSavings = budgetCategories.some(cat => cat.type === 'savings')
      const hasInvestment = budgetCategories.some(cat => cat.type === 'investment')

      if (!hasEssential || !hasSavings || !hasInvestment) {
        console.warn('GeminiService: Budget is missing some category types:', {
          hasEssential,
          hasSavings,
          hasInvestment
        })
        console.warn('GeminiService: Continuing with what was parsed - no defaults added')
      }

      // Calculate totals from what was actually parsed
      const savingsTotal = budgetCategories
        .filter(cat => cat.type === 'savings')
        .reduce((sum, cat) => sum + cat.allocated, 0)

      const investmentTotal = budgetCategories
        .filter(cat => cat.type === 'investment')
        .reduce((sum, cat) => sum + cat.allocated, 0)

      // Calculate total allocation
      const totalAllocated = budgetCategories.reduce((sum, cat) => sum + cat.allocated, 0)
      const allocationRate = Math.round((totalAllocated / monthlyIncome) * 100)

      console.log('GeminiService: Total allocated:', totalAllocated, '/', monthlyIncome, '=', allocationRate + '%')

      // Create structured budget recommendation using ONLY what was parsed
      const budgetRecommendation: BudgetRecommendation = {
        recommended_budget: budgetCategories,
        savings_strategy: {
          emergency_fund_target: monthlyIncome * 6, // 6 months emergency fund
          monthly_savings_target: savingsTotal,
          savings_rate_target: Math.round((savingsTotal + investmentTotal) / monthlyIncome * 100),
          strategies: this.extractSavingsStrategies(budgetSection)
        },
        investment_suggestions: this.extractInvestmentSuggestions(budgetSection),
        optimization_tips: this.extractOptimizationTips(budgetSection),
        reasoning: this.extractReasoning(budgetSection)
      }

      console.log('GeminiService: Budget recommendation extracted successfully:', {
        categoriesCount: budgetCategories.length,
        monthlyIncome,
        savingsTotal,
        investmentTotal,
        totalAllocated,
        totalSavingsRate: budgetRecommendation.savings_strategy.savings_rate_target,
        categories: budgetCategories.map(c => `${c.name}: $${c.allocated}`)
      })

      return budgetRecommendation
    } catch (error) {
      console.error('GeminiService: Error extracting budget recommendation:', error)
      return null
    }
  }

  /**
   * Extract monthly income from conversation history or AI response
   */
  private extractMonthlyIncome(conversationHistory: ChatMessage[], aiResponse: string): number {
    // First try to find income in conversation history
    for (const message of conversationHistory.reverse()) {
      if (message.role === 'user') {
        const incomeMatch = message.content.match(/income.*?[\$]?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i) ||
                           message.content.match(/make.*?[\$]?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i) ||
                           message.content.match(/earn.*?[\$]?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i)
        
        if (incomeMatch) {
          const income = parseFloat(incomeMatch[1].replace(/,/g, ''))
          if (income > 500 && income < 50000) { // Reasonable monthly income range
            return income
          }
        }
      }
    }

    // Try to extract from AI response
    const responseIncomeMatch = aiResponse.match(/\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?) income/i) ||
                               aiResponse.match(/Monthly.*?[\$](\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i)
    
    if (responseIncomeMatch) {
      const income = parseFloat(responseIncomeMatch[1].replace(/,/g, ''))
      if (income > 500 && income < 50000) {
        return income
      }
    }

    // Default fallback
    console.log('GeminiService: Could not extract income, using default $5000')
    return 5000
  }

  /**
   * Parse budget categories from the AI response
   */
  private parseBudgetCategories(budgetSection: string, monthlyIncome: number): BudgetCategory[] {
    const categories: BudgetCategory[] = []
    
    // Enhanced patterns for budget categories - more comprehensive and order-independent
    const categoryPatterns = [
      // Pattern 1: **Category**: $1,000 (10%)
      /(?:^|\n)(?:\*\*)?(.+?)(?:\*\*)?:?\s*\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*\((\d+(?:\.\d+)?)%\)/gim,
      // Pattern 2: - Category: $1,000 (10%)
      /(?:^|\n)-\s*(.+?):\s*\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*\((\d+(?:\.\d+)?)%\)/gim,
      // Pattern 3: Category: $1,000 (10%)
      /(?:^|\n)(.+?):\s*\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*\((\d+(?:\.\d+)?)%\)/gim,
      // Pattern 4: Category $1,000 - 10%
      /(?:^|\n)(.+?)\s+\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*[-–]\s*(\d+(?:\.\d+)?)%/gim,
      // Pattern 5: Category | $1,000 | 10%
      /(?:^|\n)(.+?)\s*\|\s*\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*\|\s*(\d+(?:\.\d+)?)%/gim,
      // Pattern 6: $1,000 (10%) - Category
      /(?:^|\n)\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*\((\d+(?:\.\d+)?)%\)\s*[-–]\s*(.+?)(?=\n|$)/gim,
      // Pattern 7: (10%) Category: $1,000
      /(?:^|\n)\((\d+(?:\.\d+)?)%\)\s*(.+?):\s*\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/gim
    ]

    for (const pattern of categoryPatterns) {
      let match
      while ((match = pattern.exec(budgetSection)) !== null) {
        let categoryName, amount, percentage
        
        // Handle different capture group orders based on pattern
        if (match.length === 4) {
          // Most patterns: name, amount, percentage
          if (!isNaN(parseFloat(match[1].replace(/,/g, '')))) {
            // Pattern 6: amount, percentage, name
            amount = parseFloat(match[1].replace(/,/g, ''))
            percentage = parseFloat(match[2])
            categoryName = match[3]
          } else if (!isNaN(parseFloat(match[2].replace(/,/g, '')))) {
            // Standard: name, amount, percentage
            categoryName = match[1]
            amount = parseFloat(match[2].replace(/,/g, ''))
            percentage = parseFloat(match[3])
          } else {
            // Pattern 7: percentage, name, amount
            percentage = parseFloat(match[1])
            categoryName = match[2]
            amount = parseFloat(match[3].replace(/,/g, ''))
          }
        } else {
          continue
        }

        categoryName = categoryName.trim().replace(/^\*+|\*+$/g, '').replace(/^-\s*/, '').trim()

        if (categoryName && amount > 0 && percentage > 0 && percentage <= 100) {
          const type = this.categorizeBudgetType(categoryName)
          
          // Avoid duplicate categories
          if (!categories.some(cat => cat.name.toLowerCase() === categoryName.toLowerCase())) {
            categories.push({
              name: categoryName,
              allocated: amount,
              percentage: Math.round(percentage),
              type: type,
              description: this.getCategoryDescription(categoryName)
            })
            console.log(`Parsed: ${categoryName}: $${amount} (${percentage}%)`)
          }
        }
      }
    }

    // If no categories found with percentages, try patterns without percentages
    if (categories.length === 0) {
      console.log('GeminiService: No categories with percentages found, trying simpler patterns')
      const simplePatterns = [
        /(?:^|\n)(?:-\s*)?(.+?):\s*\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/gim,
        /(?:^|\n)(.+?)\s+\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/gim,
        /\$(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s+(?:for\s+)?(.+?)(?:\n|$)/gim
      ]

      for (const pattern of simplePatterns) {
        let match
        while ((match = pattern.exec(budgetSection)) !== null) {
          let categoryName, amount
          
          // Handle different capture group orders
          if (match[1] && match[2]) {
            if (isNaN(parseFloat(match[1].replace(/,/g, '')))) {
              categoryName = match[1]
              amount = parseFloat(match[2].replace(/,/g, ''))
            } else {
              amount = parseFloat(match[1].replace(/,/g, ''))
              categoryName = match[2]
            }
          } else {
            continue
          }

          categoryName = categoryName.trim().replace(/^\*+|\*+$/g, '').replace(/^-\s*/, '')
          
          if (categoryName && amount > 0 && amount <= monthlyIncome) {
            const percentage = Math.round((amount / monthlyIncome) * 100)
            const type = this.categorizeBudgetType(categoryName)
            
            // Avoid duplicates
            if (!categories.some(cat => cat.name.toLowerCase() === categoryName.toLowerCase())) {
              categories.push({
                name: categoryName,
                allocated: amount,
                percentage: percentage,
                type: type,
                description: this.getCategoryDescription(categoryName)
              })
            }
          }
        }
        
        // If we found categories with this pattern, stop trying other patterns
        if (categories.length > 0) break
      }
    }

    return categories
  }

  /**
   * Categorize budget items by type
   */
  private categorizeBudgetType(categoryName: string): 'essential' | 'discretionary' | 'savings' | 'investment' {
    const name = categoryName.toLowerCase()
    
    if (name.includes('housing') || name.includes('rent') || name.includes('mortgage') ||
        name.includes('utilities') || name.includes('food') || name.includes('groceries') ||
        name.includes('transportation') || name.includes('healthcare') || name.includes('insurance')) {
      return 'essential'
    }
    
    if (name.includes('saving') || name.includes('emergency') || name.includes('fund')) {
      return 'savings'
    }
    
    if (name.includes('investment') || name.includes('invest') || name.includes('401k') ||
        name.includes('ira') || name.includes('stocks') || name.includes('retirement')) {
      return 'investment'
    }
    
    return 'discretionary'
  }

  /**
   * Get description for budget category
   */
  private getCategoryDescription(categoryName: string): string {
    const name = categoryName.toLowerCase()
    
    const descriptions: Record<string, string> = {
      'housing': 'Rent, utilities, insurance',
      'food': 'Groceries and dining',
      'transportation': 'Car payment, gas, maintenance',
      'healthcare': 'Medical expenses and insurance',
      'entertainment': 'Movies, hobbies, subscriptions',
      'savings': 'Building emergency fund',
      'investment': 'Long-term investment portfolio',
      'personal': 'Personal care and miscellaneous'
    }

    for (const [key, desc] of Object.entries(descriptions)) {
      if (name.includes(key)) {
        return desc
      }
    }

    return 'Budget allocation for ' + categoryName.toLowerCase()
  }

  /**
   * Extract savings strategies from budget section
   */
  private extractSavingsStrategies(budgetSection: string): string[] {
    const strategies = [
      'Automate savings transfers on payday for consistency',
      'Use high-yield savings account for emergency fund (2-4% APY)',
      'Start investing early - even $50/month compounds significantly over time',
      'Prioritize both emergency savings AND long-term investments',
      'Track progress monthly and increase contributions when income grows'
    ]
    
    // Try to extract specific strategies from the AI response
    const strategyKeywords = ['automate', 'high-yield', 'track', 'increase', 'compound', 'invest early']
    const extractedStrategies: string[] = []
    
    strategyKeywords.forEach(keyword => {
      if (budgetSection.toLowerCase().includes(keyword)) {
        const sentences = budgetSection.split(/[.!?]/)
        const relevantSentence = sentences.find(s => s.toLowerCase().includes(keyword))
        if (relevantSentence && relevantSentence.trim().length > 20) {
          extractedStrategies.push(relevantSentence.trim())
        }
      }
    })
    
    // Combine default and extracted strategies, avoiding duplicates
    return [...strategies, ...extractedStrategies.slice(0, 2)].slice(0, 6)
  }

  /**
   * Extract investment suggestions from budget section
   */
  private extractInvestmentSuggestions(budgetSection: string): any[] {
    const defaultSuggestions = [
      {
        type: 'Index Funds',
        allocation_percentage: 70,
        risk_level: 'moderate' as const,
        description: 'Low-cost broad market index funds',
        reasons: ['Diversification', 'Low fees', 'Long-term growth']
      },
      {
        type: 'Bonds',
        allocation_percentage: 20,
        risk_level: 'low' as const,
        description: 'Government and corporate bonds',
        reasons: ['Stability', 'Income generation', 'Risk balance']
      },
      {
        type: 'Individual Stocks',
        allocation_percentage: 10,
        risk_level: 'high' as const,
        description: 'Selected individual company stocks',
        reasons: ['Higher growth potential', 'Learning opportunity']
      }
    ]
    
    // Check if AI mentioned specific investment types
    const investmentKeywords = {
      'index fund': 'Index Funds',
      'etf': 'ETFs',
      'bond': 'Bonds',
      'stock': 'Individual Stocks',
      '401k': 'Retirement Accounts',
      'ira': 'Retirement Accounts',
      'roth': 'Roth IRA'
    }
    
    const mentionedInvestments = Object.entries(investmentKeywords)
      .filter(([keyword]) => budgetSection.toLowerCase().includes(keyword))
      .map(([_, type]) => type)
    
    // If AI mentioned specific investments, prioritize those in the allocation
    if (mentionedInvestments.length > 0) {
      console.log('GeminiService: AI mentioned specific investments:', mentionedInvestments)
    }
    
    return defaultSuggestions
  }

  /**
   * Extract optimization tips from budget section
   */
  private extractOptimizationTips(budgetSection: string): string[] {
    const defaultTips = [
      'Review and adjust your budget monthly',
      'Automate your savings and investments',
      'Look for ways to reduce fixed expenses',
      'Increase your income through side hustles or skill development',
      'Use cashback and rewards programs strategically'
    ]
    
    // Try to extract specific tips from the AI response
    const tipKeywords = ['reduce', 'increase', 'optimize', 'save on', 'cut', 'tip', 'strategy']
    const extractedTips: string[] = []
    
    // Look for bullet points or numbered lists that might contain tips
    const bulletPoints = budgetSection.match(/(?:^|\n)(?:\*|-|\d+\.)\s*(.+?)(?=\n|$)/gm)
    
    if (bulletPoints) {
      bulletPoints.forEach(point => {
        const cleanPoint = point.replace(/^(\*|-|\d+\.)\s*/, '').trim()
        if (cleanPoint.length > 10 && tipKeywords.some(keyword => 
          cleanPoint.toLowerCase().includes(keyword))) {
          extractedTips.push(cleanPoint)
        }
      })
    }
    
    // Combine and limit to 5 tips maximum
    return [...defaultTips, ...extractedTips.slice(0, 2)].slice(0, 5)
  }

  /**
   * Extract reasoning from budget section
   */
  private extractReasoning(budgetSection: string): string {
    // Try to find reasoning text in the budget section
    const reasoningPatterns = [
      /(?:reasoning|explanation|why)[\s\S]*?(?=\n\n|\n\*|$)/i,
      /(?:this budget|this plan|this allocation)[\s\S]*?(?=\n\n|\n\*|$)/i,
      /(?:^|\n)(.{50,200}(?:because|since|to ensure|in order to)[\s\S]*?)(?=\n\n|\n\*|$)/im
    ]
    
    for (const pattern of reasoningPatterns) {
      const reasoningMatch = budgetSection.match(pattern)
      if (reasoningMatch) {
        const reasoning = reasoningMatch[0].trim()
        if (reasoning.length > 30) {
          return reasoning
        }
      }
    }
    
    // If no specific reasoning found, generate based on content
    const hasSavingsFocus = budgetSection.toLowerCase().includes('saving')
    const hasInvestmentFocus = budgetSection.toLowerCase().includes('invest')
    
    let reasoning = 'Budget recommendation based on your income and financial goals'
    
    if (hasSavingsFocus && hasInvestmentFocus) {
      reasoning += ', prioritizing both emergency savings and long-term investments for comprehensive wealth building.'
    } else if (hasSavingsFocus) {
      reasoning += ', with strong emphasis on building your savings foundation.'
    } else if (hasInvestmentFocus) {
      reasoning += ', focusing on investment growth for long-term wealth building.'
    } else {
      reasoning += ', designed to balance essential expenses with future financial security.'
    }
    
    return reasoning
  }

  /**
   * Clean AI message by removing internal markers and fixing formatting
   */
  private cleanMessageForDisplay(message: string): string {
    let cleaned = message
    
    // Remove budget recommendation markers completely from display
    cleaned = cleaned.replace(/BUDGET_RECOMMENDATION_START\s*/g, '')
    cleaned = cleaned.replace(/\s*BUDGET_RECOMMENDATION_END/g, '')
    
    // Fix the "K" prefix issue for numbers - more comprehensive patterns
    // K25,000 -> $25,000
    cleaned = cleaned.replace(/\bK(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\b/g, '$$$1')
    // K25000 -> $25,000 (without commas)
    cleaned = cleaned.replace(/\bK(\d{4,})\b/g, (_match, number) => {
      const formattedNumber = parseInt(number).toLocaleString()
      return `$${formattedNumber}`
    })
    // K25.5 thousand -> $25.5 thousand
    cleaned = cleaned.replace(/\bK(\d+(?:\.\d+)?)\s*(thousand|million|billion)\b/gi, '$$$1 $2')
    // Handle edge cases like "K5" -> "$5"
    cleaned = cleaned.replace(/\bK(\d+)\b/g, '$$$1')
    
    // Clean up extra whitespace and newlines caused by marker removal
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n')
    cleaned = cleaned.replace(/^\s*\n+/g, '') // Remove leading newlines
    cleaned = cleaned.replace(/\n+\s*$/g, '') // Remove trailing newlines
    cleaned = cleaned.trim()
    
    return cleaned
  }

  private createBudgetAnalysisPrompt(profile: FinancialProfile): string {
    const expenseBreakdown = profile.expenses
      .map(exp => `- ${exp.category}: $${exp.amount}/${exp.frequency}`)
      .join('\n')

    return `Please create a detailed budget recommendation for a user with the following financial profile:

Monthly Income: $${profile.monthly_income}
Current Expenses:
${expenseBreakdown}

Current Savings Rate: ${profile.savings_rate}%
Risk Tolerance: ${profile.risk_tolerance}
Financial Goals: ${profile.financial_goals.join(', ')}

Please provide a structured response with:
1. Recommended budget categories with specific dollar amounts and percentages
2. Savings strategy with target amounts and methods
3. Investment suggestions based on risk tolerance
4. 3-5 specific optimization tips
5. Clear reasoning for your recommendations

Format your response as a clear, actionable plan that prioritizes saving and investing while maintaining a realistic lifestyle.`
  }

  private parseBudgetRecommendation(aiResponse: string, profile: FinancialProfile): BudgetRecommendation {
    // This is a simplified parser - in a real implementation, you might want to use
    // function calling or structured output from Gemini
    
    const totalIncome = profile.monthly_income
    
    // Enhanced budget allocation prioritizing both savings AND investments (30% combined minimum)
    const budgetCategories = [
      {
        name: 'Housing',
        allocated: Math.round(totalIncome * 0.25),
        percentage: 25,
        type: 'essential' as const,
        description: 'Rent, utilities, insurance'
      },
      {
        name: 'Food',
        allocated: Math.round(totalIncome * 0.10),
        percentage: 10,
        type: 'essential' as const,
        description: 'Groceries and dining'
      },
      {
        name: 'Transportation',
        allocated: Math.round(totalIncome * 0.08),
        percentage: 8,
        type: 'essential' as const,
        description: 'Car payment, gas, maintenance'
      },
      {
        name: 'Personal & Healthcare',
        allocated: Math.round(totalIncome * 0.05),
        percentage: 5,
        type: 'essential' as const,
        description: 'Healthcare, personal care'
      },
      {
        name: 'Entertainment & Discretionary',
        allocated: Math.round(totalIncome * 0.17),
        percentage: 17,
        type: 'discretionary' as const,
        description: 'Entertainment, hobbies, miscellaneous'
      },
      {
        name: 'Emergency Savings',
        allocated: Math.round(totalIncome * 0.15),
        percentage: 15,
        type: 'savings' as const,
        description: 'Building 6-month emergency fund'
      },
      {
        name: 'Retirement Savings (401k/IRA)',
        allocated: Math.round(totalIncome * 0.12),
        percentage: 12,
        type: 'investment' as const,
        description: 'Long-term retirement accounts'
      },
      {
        name: 'Investment Portfolio',
        allocated: Math.round(totalIncome * 0.08),
        percentage: 8,
        type: 'investment' as const,
        description: 'Index funds, stocks, ETFs for wealth building'
      }
    ]

    const savingsStrategy = {
      emergency_fund_target: totalIncome * 6, // 6 months of expenses
      monthly_savings_target: Math.round(totalIncome * 0.35), // 35% savings rate (15% savings + 20% investment)
      savings_rate_target: 35,
      strategies: [
        'Automate savings transfers on payday for consistency',
        'Use high-yield savings account for emergency fund (2-4% APY)',
        'Start investing early - even $50/month compounds significantly over time',
        'Prioritize both emergency savings AND long-term investments',
        'Track progress monthly and increase contributions when income grows'
      ]
    }

    const investmentSuggestions = [
      {
        type: 'Index Funds',
        allocation_percentage: 70,
        risk_level: 'moderate' as const,
        description: 'Low-cost broad market index funds',
        reasons: ['Diversification', 'Low fees', 'Long-term growth']
      },
      {
        type: 'Bonds',
        allocation_percentage: 20,
        risk_level: 'low' as const,
        description: 'Government and corporate bonds',
        reasons: ['Stability', 'Income generation', 'Risk balance']
      },
      {
        type: 'Individual Stocks',
        allocation_percentage: 10,
        risk_level: 'high' as const,
        description: 'Selected individual company stocks',
        reasons: ['Higher growth potential', 'Learning opportunity']
      }
    ]

    return {
      recommended_budget: budgetCategories,
      savings_strategy: savingsStrategy,
      investment_suggestions: investmentSuggestions,
      optimization_tips: [
        'Review and adjust your budget monthly',
        'Automate your savings and investments',
        'Look for ways to reduce fixed expenses',
        'Increase your income through side hustles or skill development',
        'Use cashback and rewards programs strategically'
      ],
      reasoning: aiResponse || 'Based on your income and goals, this budget prioritizes building wealth through consistent saving and investing while maintaining a comfortable lifestyle.'
    }
  }

  // Utility method to check if service is available
  isAvailable(): boolean {
    return !!this.genAI && !!this.config.apiKey && this.isInitialized
  }

  // Async method to check if service will be available
  async checkAvailability(): Promise<boolean> {
    if (!this.genAI || !this.config.apiKey) {
      return false
    }

    if (this.initializationPromise) {
      await this.initializationPromise
    }

    return this.isInitialized
  }

  // Get service configuration
  getConfig(): GeminiConfig {
    return { ...this.config }
  }

  // Get current model and cost information
  getCurrentModelInfo(): { model: string; costInfo: string; isInitialized: boolean } {
    return {
      model: this.config.model || 'Not selected',
      costInfo: this.config.model ? this.getModelCostInfo(this.config.model) : 'Unknown',
      isInitialized: this.isInitialized
    }
  }

  // Force re-initialize with new cost control (FLASH ONLY)
  async forceReInitialize(): Promise<void> {
    console.log('Force re-initializing with FLASH-ONLY cost control...')
    
    this.isInitialized = false
    this.model = null
    this.config.model = ''
    
    if (this.genAI) {
      await this.initializeWithWorkingModel()
    } else {
      console.error('Cannot reinitialize - no GoogleGenerativeAI instance')
    }
  }

  // Quiz Generation Methods
  async generateQuizQuestions(videoInfo: {
    title: string
    description?: string
    transcript?: string
    learningObjectives: string[]
    category: string
    difficulty: string
  }): Promise<QuizQuestion[]> {
    // Wait for initialization to complete
    if (this.initializationPromise) {
      await this.initializationPromise
    }

    if (!this.model || !this.isInitialized) {
      throw new Error('Gemini service not initialized. Please check your API key and internet connection.')
    }

    try {
      const prompt = this.createQuizGenerationPrompt(videoInfo)
      
      const result = await this.model.generateContent(prompt)
      const response = await result.response
      const text = response.text()

      // Parse the structured response
      return this.parseQuizQuestions(text, videoInfo)
    } catch (error) {
      console.error('Quiz generation error:', error)
      throw new Error(`Failed to generate quiz questions: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  private createQuizGenerationPrompt(videoInfo: {
    title: string
    description?: string
    transcript?: string
    learningObjectives: string[]
    category: string
    difficulty: string
  }): string {
    const { title, description, transcript, learningObjectives, category, difficulty } = videoInfo

    return `You are an expert educational content creator specializing in financial literacy and Federal Reserve education. Generate 5 high-quality quiz questions for the following educational video.

VIDEO INFORMATION:
Title: ${title}
Description: ${description || 'Not provided'}
Category: ${category}
Difficulty: ${difficulty}
Learning Objectives: ${learningObjectives.join(', ')}

${transcript ? `TRANSCRIPT:\n${transcript}` : 'No transcript provided'}

REQUIREMENTS:
1. Generate exactly 5 questions
2. Question distribution:
   - 3 multiple-choice questions (4 options each)
   - 2 true/false questions
3. Difficulty distribution:
   - 2 easy questions (basic recall)
   - 2 medium questions (understanding concepts)
   - 1 hard question (application/analysis)
4. All questions must align with the learning objectives
5. Focus on Federal Reserve and financial literacy concepts
6. Make questions clear, unambiguous, and educational

OUTPUT FORMAT:
Return your response in the following JSON format:
{
  "questions": [
    {
      "question_text": "What is the primary purpose of the Federal Reserve?",
      "question_type": "multiple-choice",
      "options": ["A) Regulate banks only", "B) Control inflation and employment", "C) Print money", "D) Collect taxes"],
      "correct_answer": "B) Control inflation and employment",
      "explanation": "The Federal Reserve's dual mandate is to promote maximum employment and stable prices (control inflation).",
      "difficulty": "easy",
      "order_index": 1
    },
    {
      "question_text": "The Federal Reserve can directly control the federal funds rate.",
      "question_type": "true-false",
      "correct_answer": "True",
      "explanation": "The Fed sets a target for the federal funds rate and uses open market operations to influence it.",
      "difficulty": "medium",
      "order_index": 2
    }
    // ... continue for all 5 questions
  ]
}

IMPORTANT:
- Ensure all multiple-choice questions have exactly 4 options
- Use clear, educational explanations for all answers
- Make questions specific to Federal Reserve and financial concepts
- Avoid trick questions or overly complex wording
- Ensure questions test understanding, not just memorization

Generate the quiz now:`
  }

  private parseQuizQuestions(aiResponse: string, videoInfo: { category: string }): QuizQuestion[] {
    try {
      // Try to extract JSON from the response
      const jsonMatch = aiResponse.match(/\{[\s\S]*\}/)
      if (!jsonMatch) {
        throw new Error('No JSON found in AI response')
      }

      const parsed = JSON.parse(jsonMatch[0])
      
      if (!parsed.questions || !Array.isArray(parsed.questions)) {
        throw new Error('Invalid response format - no questions array found')
      }

      // Validate and format questions
      const questions: QuizQuestion[] = parsed.questions.map((q: any, index: number) => {
        // Validate required fields
        if (!q.question_text || !q.question_type || !q.correct_answer) {
          throw new Error(`Question ${index + 1} missing required fields`)
        }

        // Validate question type
        if (!['multiple-choice', 'true-false', 'short-answer'].includes(q.question_type)) {
          throw new Error(`Question ${index + 1} has invalid question_type: ${q.question_type}`)
        }

        // Validate difficulty
        if (!['easy', 'medium', 'hard'].includes(q.difficulty)) {
          q.difficulty = 'medium' // Default fallback
        }

        // For multiple-choice questions, validate options
        if (q.question_type === 'multiple-choice') {
          if (!q.options || !Array.isArray(q.options) || q.options.length !== 4) {
            throw new Error(`Question ${index + 1} is multiple-choice but doesn't have exactly 4 options`)
          }
        }

        return {
          id: `temp_${Date.now()}_${index}`, // Temporary ID for new questions
          video_id: 'temp', // Will be set when saving to database
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          options: q.options || undefined,
          correct_answer: q.correct_answer.trim(),
          explanation: q.explanation?.trim() || '',
          difficulty: q.difficulty,
          order_index: q.order_index || index + 1,
          created_at: new Date().toISOString()
        }
      })

      if (questions.length !== 5) {
        throw new Error(`Expected 5 questions, got ${questions.length}`)
      }

      console.log('Successfully parsed quiz questions:', questions.length)
      return questions

    } catch (error) {
      console.error('Error parsing quiz questions:', error)
      
      // Fallback: Generate basic questions if parsing fails
      return this.generateFallbackQuestions({ title: videoInfo.title || 'Video', category: videoInfo.category })
    }
  }

  private generateFallbackQuestions(videoInfo: { title: string; category: string }): QuizQuestion[] {
    const { title, category } = videoInfo
    
    return [
      {
        id: `fallback_${Date.now()}_1`,
        video_id: 'temp',
        question_text: `What is the main topic covered in "${title}"?`,
        question_type: 'multiple-choice',
        options: [
          'A) Banking regulations',
          'B) Monetary policy',
          'C) Personal finance',
          'D) Investment strategies'
        ],
        correct_answer: 'B) Monetary policy',
        explanation: 'This video covers monetary policy concepts.',
        difficulty: 'easy',
        order_index: 1,
        created_at: new Date().toISOString()
      },
      {
        id: `fallback_${Date.now()}_2`,
        video_id: 'temp',
        question_text: 'The Federal Reserve plays a key role in the U.S. economy.',
        question_type: 'true-false',
        correct_answer: 'True',
        explanation: 'The Federal Reserve is the central bank of the United States and plays a crucial role in monetary policy.',
        difficulty: 'easy',
        order_index: 2,
        created_at: new Date().toISOString()
      },
      {
        id: `fallback_${Date.now()}_3`,
        video_id: 'temp',
        question_text: `What category does this video belong to: ${category}?`,
        question_type: 'multiple-choice',
        options: [
          'A) Entertainment',
          'B) Educational',
          'C) Commercial',
          'D) News'
        ],
        correct_answer: 'B) Educational',
        explanation: 'This is an educational video about financial concepts.',
        difficulty: 'easy',
        order_index: 3,
        created_at: new Date().toISOString()
      },
      {
        id: `fallback_${Date.now()}_4`,
        video_id: 'temp',
        question_text: 'Understanding financial concepts helps with personal decision-making.',
        question_type: 'true-false',
        correct_answer: 'True',
        explanation: 'Financial literacy enables better personal financial decisions.',
        difficulty: 'medium',
        order_index: 4,
        created_at: new Date().toISOString()
      },
      {
        id: `fallback_${Date.now()}_5`,
        video_id: 'temp',
        question_text: 'What is the primary purpose of financial education?',
        question_type: 'multiple-choice',
        options: [
          'A) To make money',
          'B) To help people make informed financial decisions',
          'C) To sell financial products',
          'D) To replace professional advice'
        ],
        correct_answer: 'B) To help people make informed financial decisions',
        explanation: 'Financial education aims to empower individuals with knowledge for better financial decision-making.',
        difficulty: 'medium',
        order_index: 5,
        created_at: new Date().toISOString()
      }
    ]
  }


}

// Create a singleton instance
export const geminiService = new GeminiService()

// Export the class for testing or custom instances
export default GeminiService
